// src/services/xpService.js
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { logFirebaseError } from "../utils/fbError";


// small unique suffix to avoid eventId collisions
const uniq = () => `${Date.now()}_${Math.random().toString(36).slice(2,10)}`;


export const XP_RULES = {
  LIKE_GIVE: 1, // client can send, CF ignores for LIKE_GIVE path
  LIKE_RECEIVE: 2, // server-side only
  COMMENT: 2,
  COMMENT_RECEIVE: 2,
  CREATE_POST: 3,
};


/** Enqueue an XP event (idempotent by eventId). CF applies the XP. */
 export async function enqueueXPEvent({
  uid,
  type,
  amount = 0,
  amountReceiver = 0,   // ✅ NEW
  eventId,
  meta = {},
}) {
  if (!uid || !type || !eventId) return;

  const ref = doc(db, "xp_events", eventId);
  try {
    await setDoc(
      ref,
      {
        uid,
        type, // e.g., "CREATE_POST_BLIP" | "LIKE_GIVE" | "LIKE_UNDO"
        amount: Number(amount) || 0, // CF will clamp/ignore depending on type
        amountReceiver: Number(amountReceiver) || 0, // ✅ CF generic path reads this
        meta, // e.g., { postId, postOwnerUid }
        createdAt: serverTimestamp(),
      },
      { merge: false } // idempotent by unique eventId
    );
  } catch (err) {
    logFirebaseError("enqueueXPEvent", err, {
      uid,
      type,
      amount,
      eventId,
      meta,
    });
    throw err;
  }
}

/** Generic wrapper to build a stable event id from a dedupe key. */
export async function awardXP(uid, amount, type, dedupeKey, meta = {}) {
  if (!uid || !amount || !type || !dedupeKey) return;
  const eventId = `${type}:${dedupeKey}:${uid}`;
  return enqueueXPEvent({ uid, type, amount, eventId, meta });
}

/* ------------------------------------------------------------------ */
/* Optional helpers you can use in your UI                            */
/* ------------------------------------------------------------------ */

/** Call when user taps Like. */
export async function enqueueLikeGive({ giverUid, postId, postOwnerUid, postCollection, likeModel }) {
  if (!giverUid || !postId) return;
  return enqueueXPEvent({
    uid: giverUid,
    type: "LIKE_GIVE",
    amount: 1, // ignored by CF for LIKE_GIVE
    eventId: `like:${postId}:${giverUid}:${Date.now()}`,
    meta: { postId, postOwnerUid, postCollection, likeModel },
  });
}

/** Call when user untaps Like. Use a unique id so multiple unlikes can be tracked safely. */
export async function enqueueLikeUndo({ giverUid, postId, postOwnerUid, postCollection, likeModel }) {
  if (!giverUid || !postId) return;
  return enqueueXPEvent({
    uid: giverUid,
    type: "LIKE_UNDO",
    amount: -1, // ignored by CF for LIKE_UNDO
    eventId: `like_undo:${postId}:${giverUid}:${Date.now()}`, // unique
    meta: { postId, postOwnerUid, postCollection, likeModel },
  });
}

export async function enqueueReactionGive({ giverUid, postId, postOwnerUid, postCollection="missions", reactionId }) {
  return enqueueXPEvent({
    uid: giverUid,
    type: "REACTION_GIVE",
    amount: 1,
    amountReceiver: 2,
    eventId: `react:${reactionId}:${postId}:${giverUid}:${Date.now()}`,
    meta: { postId, postOwnerUid, postCollection, reactionId },
  });
}

export async function enqueueReactionUndo({ giverUid, postId, postOwnerUid, postCollection="missions", reactionId }) {
  return enqueueXPEvent({
    uid: giverUid,
    type: "REACTION_UNDO",
    amount: -1,
    amountReceiver: -2,
    eventId: `react_undo:${reactionId}:${postId}:${giverUid}:${Date.now()}`,
    meta: { postId, postOwnerUid, postCollection, reactionId },
  });
}

/** Mission completion: giver +3, creator +1 (tweak amounts as you like; CF clamps ±5) */
export async function enqueueMissionComplete({ uid, missionId, ownerUid }) {
  if (!uid || !missionId) return;
  return enqueueXPEvent({
    uid,
    type: "MISSION_COMPLETE",
    amount: 3,
    amountReceiver: ownerUid && ownerUid !== uid ? 1 : 0,
    eventId: `mission_complete:${missionId}:${uid}:${uniq()}`,
    meta: {
      postId: missionId,
      postOwnerUid: ownerUid || null,
      postCollection: "missions",
    },
  });
}

/** Mission un-complete (undo): giver −3, creator −1 (mirrors the above) */
export async function enqueueMissionUncomplete({ uid, missionId, ownerUid }) {
  if (!uid || !missionId) return;
  return enqueueXPEvent({
    uid,
    type: "MISSION_UNCOMPLETE",
    amount: -3,
    amountReceiver: ownerUid && ownerUid !== uid ? -1 : 0,
    eventId: `mission_uncomplete:${missionId}:${uid}:${uniq()}`,
    meta: {
      postId: missionId,
      postOwnerUid: ownerUid || null,
      postCollection: "missions",
    },
  });
}